import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Shield, Plus, Brain, Loader2, Users } from 'lucide-react';
import { ExpertCard } from '../components/experts/ExpertCard';
import { CrossAnalysisResult } from '../components/experts/CrossAnalysisResult';
import { ArgumentRanking } from '../components/experts/ArgumentRanking';
import { DoctrinePanel } from '../components/experts/DoctrinePanel';
import { NotarialRulings } from '../components/experts/NotarialRulings';
import { runAiJob } from '../lib/aiJob';

const INPUT = 'w-full bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-sm';

export default function ExpertsPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [experts, setExperts] = useState([]);
  const [matters, setMatters] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selected, setSelected] = useState([]);
  const [newName, setNewName] = useState('');
  const [matterId, setMatterId] = useState('');
  const [caseText, setCaseText] = useState('');
  const [result, setResult] = useState(null);
  const [running, setRunning] = useState(false);

  const load = () => api.get('/experts').then((r) => setExperts(r.data.experts));
  useEffect(() => {
    load();
    api.get('/crm/matters?limit=100').then((r) => setMatters(r.data.matters || [])).catch(() => {});
    api.get('/admin/check').then((r) => setIsAdmin(!!r.data.is_admin)).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  const add = async () => {
    if (newName.trim().length < 5) return toast.error('Escribe el nombre completo');
    try {
      await api.post('/experts', { name: newName.trim() });
      setNewName('');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'No se pudo agregar'); }
  };

  const analyze = async () => {
    setRunning(true);
    setResult(null);
    try {
      setResult(await runAiJob(api, '/experts/cross-analysis', {
        matter_id: matterId || null, case_text: matterId ? null : caseText || null, expert_ids: selected.length ? selected : null
      }));
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || 'No se pudo analizar');
    } finally { setRunning(false); }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <header className="border-b border-slate-800 bg-slate-900/80 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center gap-3">
          <button data-testid="experts-back-btn" onClick={() => navigate('/dashboard')}><Shield className="w-8 h-8 text-blue-500" /></button>
          <span className="text-xl font-bold">Expertos Notariales y Registrales</span>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 py-8">
        <p className="text-slate-400 text-sm mb-6 max-w-3xl">Cerebro reúne los casos públicos y los documentos que subas de los mejores expertos en derecho notarial y registral, cruza sus victorias con sus derrotas y arma la estrategia con más opciones de ganar, con su probabilidad real.</p>
        <div className="flex gap-2 mb-4 max-w-xl">
          <input data-testid="expert-new-name-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Agregar experto (nombre completo)" className={INPUT} />
          <button data-testid="expert-add-btn" onClick={add} className="shrink-0 bg-slate-700 hover:bg-slate-600 rounded-lg px-3 text-sm flex items-center gap-1"><Plus className="w-4 h-4" />Agregar</button>
        </div>
        <div data-testid="experts-list" className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-10">
          {experts.map((e) => <ExpertCard key={e.id} expert={e} isAdmin={isAdmin} onChanged={load} onSelect={toggle} selected={selected.includes(e.id)} />)}
        </div>

        <DoctrinePanel />
        <NotarialRulings />
        <ArgumentRanking refreshKey={experts.reduce((s, e) => s + e.cases_total, 0)} />

        <section data-testid="cross-analysis-section" className="bg-slate-900/60 border border-slate-700 rounded-2xl p-6">
          <h2 className="text-lg font-bold flex items-center gap-2 mb-1"><Brain className="w-5 h-5 text-amber-400" />Victorias cruzadas → estrategia óptima</h2>
          <p className="text-xs text-slate-400 mb-4 flex items-center gap-1"><Users className="w-3 h-3" />{selected.length ? `${selected.length} experto(s) seleccionado(s)` : 'Todos los expertos'} · elige un caso del CRM o describe el caso difícil</p>
          <div className="grid md:grid-cols-2 gap-3">
            <select data-testid="cross-matter-select" value={matterId} onChange={(e) => setMatterId(e.target.value)} className={INPUT}>
              <option value="">Sin caso del CRM (describir abajo)</option>
              {matters.map((m) => <option key={m.id} value={m.id}>{m.title}</option>)}
            </select>
            {!matterId && <textarea data-testid="cross-case-text" value={caseText} onChange={(e) => setCaseText(e.target.value)} rows={2} placeholder="Ej: notario acusado de autorizar cesiones de derechos que encubren loteo irregular" className={INPUT} />}
          </div>
          <button data-testid="cross-analysis-btn" onClick={analyze} disabled={running} className="mt-4 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold rounded-full px-5 py-2 text-sm flex items-center gap-2 disabled:opacity-50">
            {running && <Loader2 className="w-4 h-4 animate-spin" />}{running ? 'Cruzando victorias y derrotas...' : 'Formular estrategia óptima'}
          </button>
          {result && <CrossAnalysisResult r={result} />}
        </section>
      </main>
    </div>
  );
}
